/**
 * The fixture identity provider. It exists so that all four named failure states, the anonymous
 * link path and the hand-off are REAL RENDERED SCREENS in the default build, which has no Firebase
 * project behind it at all. ./firebase-auth-client.ts is the real adapter; one variable chooses.
 *
 * IT IS DELIBERATELY UNFRIENDLY IN THE RIGHT PLACES. A mock that always succeeds proves only that
 * the happy path compiles. This one blocks popups on demand, cancels on demand, and — the property
 * that matters most — keeps the uid across a link, so the check in ../session/link-anonymous.ts is
 * exercised rather than merely written.
 *
 * DRIVING IT: `?mx-auth=` on the URL, or `setMockAuthScenario` from a test.
 *   signed-out   nobody is signed in (the default)
 *   restored     the SDK restored an approved author's session for this origin
 *   anonymous    the SDK restored an ANONYMOUS reader's session — the G30 path
 *   popup-blocked / popup-cancelled / provider-unreachable   the cold-start failures
 *
 * NO CLOCK IS READ. C11 bans it repo-wide and nothing here needs one: an id counter gives stable,
 * ordered, unique values without touching a global clock.
 */
import { PopupBlockedError, PopupCancelledError, ProviderUnreachableError } from './auth-port';
import type { AuthClient, AuthIdentity, AuthSession, PopupProvider } from './auth-port';

export type MockAuthScenario =
  | 'signed-out'
  | 'restored'
  | 'anonymous'
  | 'popup-blocked'
  | 'popup-cancelled'
  | 'provider-unreachable';

const MOCK_AUTH_SCENARIOS: readonly MockAuthScenario[] = [
  'signed-out',
  'restored',
  'anonymous',
  'popup-blocked',
  'popup-cancelled',
  'provider-unreachable',
];

const PROVIDER_LABELS: Readonly<Record<PopupProvider, string>> = {
  apple: 'Apple',
  google: 'Google',
};

interface MockState {
  scenario: MockAuthScenario;
  identity: AuthIdentity | null;
  /** Rises monotonically so each minted token is distinguishable without reading a clock. */
  tokenCounter: number;
  pendingMagicLinkEmail: string | null;
}

const state: MockState = {
  scenario: 'signed-out',
  identity: null,
  tokenCounter: 0,
  pendingMagicLinkEmail: null,
};

const listeners = new Set<(session: AuthSession | null) => void>();

const RESTORED_IDENTITY: AuthIdentity = {
  uid: 'fixture-uid-approved-author',
  isAnonymous: false,
  providerLabels: ['Apple'],
  displayName: 'Mr Moon',
  email: 'mr.moon@example.test',
};

const ANONYMOUS_IDENTITY: AuthIdentity = {
  uid: 'fixture-uid-anonymous-reader',
  isAnonymous: true,
  providerLabels: [],
  displayName: null,
  email: null,
};

function sessionFor(identity: AuthIdentity | null): AuthSession | null {
  if (identity === null) {
    return null;
  }
  return {
    identity,
    getIdToken: (forceRefresh?: boolean): Promise<string> => {
      state.tokenCounter += 1;
      const suffix = forceRefresh === true ? 'refreshed' : 'cached';
      return Promise.resolve(
        `fixture-id-token.${identity.uid}.${String(state.tokenCounter)}.${suffix}`,
      );
    },
  };
}

function publish(): void {
  const session = sessionFor(state.identity);
  for (const listener of listeners) {
    listener(session);
  }
}

export function setMockAuthScenario(next: MockAuthScenario): void {
  state.scenario = next;
  if (next === 'restored') {
    state.identity = RESTORED_IDENTITY;
  } else if (next === 'anonymous') {
    state.identity = ANONYMOUS_IDENTITY;
  } else if (next === 'signed-out') {
    state.identity = null;
  }
  publish();
}

export function resetMockAuth(): void {
  state.scenario = 'signed-out';
  state.identity = null;
  state.tokenCounter = 0;
  state.pendingMagicLinkEmail = null;
  publish();
}

/** `?mx-auth=` — an unknown value is ignored, because a mistyped debug flag must not break the app. */
export function applyMockAuthScenarioFromSearch(search: string): void {
  const requested = new URLSearchParams(search).get('mx-auth');
  if (requested === null) {
    return;
  }
  const match = MOCK_AUTH_SCENARIOS.find((candidate) => candidate === requested);
  if (match !== undefined) {
    setMockAuthScenario(match);
  }
}

function refusePopupIfScenarioSaysSo(): void {
  if (state.scenario === 'popup-blocked') {
    throw new PopupBlockedError();
  }
  if (state.scenario === 'popup-cancelled') {
    throw new PopupCancelledError();
  }
  if (state.scenario === 'provider-unreachable') {
    throw new ProviderUnreachableError();
  }
}

export const mockAuthClient: AuthClient = {
  currentSession: () => sessionFor(state.identity),

  subscribe: (listener) => {
    listeners.add(listener);
    // Fire once immediately, exactly as an auth-state observer does, so a consumer never has to
    // ask separately whether a session was already restored.
    listener(sessionFor(state.identity));
    return () => {
      listeners.delete(listener);
    };
  },

  signInWithPopup: (provider: PopupProvider) => {
    try {
      refusePopupIfScenarioSaysSo();
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
    state.identity = {
      ...RESTORED_IDENTITY,
      providerLabels: [PROVIDER_LABELS[provider]],
    };
    publish();
    const session = sessionFor(state.identity);
    return session === null
      ? Promise.reject(new Error('mock sign-in produced no session'))
      : Promise.resolve(session);
  },

  signInWithCustomToken: (customToken: string) => {
    // The real SDK derives the uid from the token's own claims; the fixture derives it from the
    // token's text so that a hand-off and a cold start land on visibly different accounts.
    state.identity = {
      ...RESTORED_IDENTITY,
      uid: `fixture-uid-from-handoff-${customToken.slice(-8)}`,
      providerLabels: ['MoonX app'],
    };
    publish();
    const session = sessionFor(state.identity);
    return session === null
      ? Promise.reject(new Error('mock hand-off produced no session'))
      : Promise.resolve(session);
  },

  linkWithPopup: (provider: PopupProvider) => {
    const existing = state.identity;
    if (existing === null) {
      return Promise.reject(new Error('There is no signed-in account to link a provider to.'));
    }
    try {
      refusePopupIfScenarioSaysSo();
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    // THE POINT OF THE WHOLE FIXTURE: the uid is carried over UNCHANGED. Linking keeps the same
    // account, so nothing the reader already did is orphaned. Proved against the Firebase Auth
    // emulator on 2026-08-27 — linking returned the same localId.
    state.identity = {
      ...existing,
      isAnonymous: false,
      providerLabels: [...existing.providerLabels, PROVIDER_LABELS[provider]],
    };
    publish();
    const session = sessionFor(state.identity);
    return session === null
      ? Promise.reject(new Error('mock link produced no session'))
      : Promise.resolve(session);
  },

  sendMagicLink: (email: string) => {
    state.pendingMagicLinkEmail = email;
    return Promise.resolve();
  },

  isMagicLinkUrl: (url: string) => url.includes('mx-magic-link=1'),

  completeMagicLink: (_url: string, email: string) => {
    state.identity = {
      ...RESTORED_IDENTITY,
      uid: `fixture-uid-magic-link-${email}`,
      providerLabels: ['E-mail link'],
      email,
    };
    state.pendingMagicLinkEmail = null;
    publish();
    const session = sessionFor(state.identity);
    return session === null
      ? Promise.reject(new Error('mock magic link produced no session'))
      : Promise.resolve(session);
  },

  signOut: () => {
    state.identity = null;
    state.scenario = 'signed-out';
    publish();
    return Promise.resolve();
  },
};

/** What the fixture stored when the magic link was requested. Read by the handler when the device matches. */
export function pendingMagicLinkEmail(): string | null {
  return state.pendingMagicLinkEmail;
}
